<template>
  <ul class="seats">
    <li
      v-for="seat in seats"
      :key="seat.id"
      class="seat"
      :class="{
        'seat--me': seat.id === me?.id,
        'seat--turn': isBuying && !game.meta.roundOver && game.meta.turnSeat === seat.seat,
        'seat--passed': isBuying && seat.passed,
        'seat--absent': absentPlayerIds.includes(seat.id),
      }"
    >
      <span class="seat__name">
        {{ seat.name }}<span v-if="seat.isBot" class="seat__tag">bot</span>
      </span>

      <span class="seat__stats">
        <!-- IELLO p3: "You should keep your coins hidden." Your own money is
             yours to see; everyone else's is a ? until the end. -->
        <span class="seat__coins" :title="coinsTitle(seat)">
          <i class="coin" aria-hidden="true" />{{ seat.id === me?.id ? money(seat.coins) : '?' }}
        </span>

        <!-- Houses in hand are public - they were bought in the open. -->
        <span
          class="seat__houses"
          :title="housesTitle(seat)"
        ><i class="house" aria-hidden="true" />{{ seat.properties.length }}</span>

        <!-- Cheques won are face down (IELLO p7), so only the count shows. -->
        <span
          v-if="seat.cheques.length"
          class="seat__cheques"
          :title="`${seat.cheques.length} cheques, face down`"
        ><i class="cheque" aria-hidden="true" />{{ seat.cheques.length }}</span>
      </span>

      <span class="seat__state" :class="stateClass(seat)">{{ stateLabel(seat) }}</span>
    </li>
  </ul>
</template>

<script>
import { mapState, mapGetters } from 'vuex';
import { cardById } from '@/engine/deck';
import { PHASES } from '@/engine/constants';

// Everyone at the table: their bid, their hand, and whether the table is
// waiting on them.
//
// THE ONE RULE THIS COMPONENT EXISTS TO ENFORCE, inherited from incan-gold's
// SeatList: in the selling phase nobody's pick is shown until every single
// pick is in. They are written to the shared state one at a time as people
// tap, so rendering them as they arrive would hand the slowest picker
// everyone else's card - which is the entire phase. Until then a seat reads
// only "ready" or "picking…". (The reveal itself happens in the round
// summary, so even after the last pick lands this list only ever says
// "ready".)
export default {
  name: 'SeatList',
  computed: {
    ...mapState(['game', 'roomMeta']),
    ...mapGetters(['me', 'others', 'isBuying', 'absentPlayerIds']),
    /** Me first, then everyone else round to the left. */
    seats() {
      return this.me ? [this.me, ...this.others] : this.game?.players || [];
    },
  },
  methods: {
    money(thousands) {
      return `$${(thousands * 1000).toLocaleString('en-US')}`;
    },
    coinsTitle(seat) {
      return seat.id === this.me?.id
        ? 'Your money - counts at face value at the end'
        : 'Their money. Hidden until the end.';
    },
    housesTitle(seat) {
      const values = seat.properties.map((id) => cardById(id).value).sort((a, b) => a - b);
      return values.length ? `Houses in hand: ${values.join(', ')}` : 'No houses yet';
    },
    stateLabel(seat) {
      if (this.game.meta.roundOver) return '';
      if (this.game.meta.phase === PHASES.BUYING) {
        if (seat.passed) return 'out this round';
        if (seat.bid > 0) return `bid ${this.money(seat.bid)}`;
        if (this.game.meta.turnSeat === seat.seat) {
          return this.absentPlayerIds.includes(seat.id) ? 'gone quiet' : 'deciding…';
        }
        return '';
      }
      if (this.game.meta.phase !== PHASES.SELLING) return '';
      if (seat.pick !== null) return 'ready';
      return this.absentPlayerIds.includes(seat.id) ? 'gone quiet' : 'picking…';
    },
    stateClass(seat) {
      if (this.game.meta.roundOver) return null;
      if (this.game.meta.phase === PHASES.BUYING && seat.bid > 0 && !seat.passed) return 'seat__state--bid';
      if (this.game.meta.phase === PHASES.SELLING && seat.pick !== null) return 'seat__state--ready';
      return null;
    },
  },
};
</script>
